import { ReactElement, useState } from 'react';
import { NavigatorTab } from '@components/common/navigator-tab';
import {
	ISiteSummaryList,
	RBDMSiteSummaryTab,
	SiteSummaryTab,
	SiteSummaryTabKAESSUser,
	SiteSummaryTabKAEUser,
} from '@components/siteSummary/index';
import {
	SiteSummaryCard,
	SiteSummaryFilters,
	SiteSummaryFilterTag,
	SiteSummarySearch,
} from '@components/siteSummary/components';
import { useSiteSummaryListing } from '@services/hooks/site-summary-listing';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useSiteSummaryRxDb } from '@services/hooks/site-summary-listing/useSiteSummaryRxDb';
import { AddNewDataButton, SiteStatusModal, Sort, useInfiniteScroll } from '@components/common';
import { NoDataFound } from '@components/common/no-data-found/noDataFound';
import { useFilterStore } from '@store/filter';
import { overlayAddFadeAction, overlayRemoveFadeAction } from '@components/common/sort';
import { useSiteSummaryListingStore } from '@store/siteSummaryListingFilterStore';
import { Modal } from '@components/common/modal/modal';
import { useContractor360Store } from '@store/contractor360Store';
import { useFormik } from 'formik';
import useStepperStore from '@store/stepperStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { useComponentLevelAccessCheck } from '../../authGuard/hooks/useComponentLevelAccessCheck';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { APIService } from '@services/api/common-services';
import { APICONFIG } from '@config/api.config';
import { DataRow, formatDateToDDMMYYYY } from '@helper/utils';
import { ExportXlsxFile } from '@helper/exportXlsxFile';
import { ToastType, useTosterStore } from '@store/toster';
import { useIsImpersonationLoginUser } from '../../authGuard/hooks/useIsImpersonationLoginUser';

export const SiteSummaryListing = (): ReactElement => {
	const [openSort, setOpenSort] = useState<boolean>(false);
	const [openFilter, setOpenFilter] = useState<boolean>(false);
	const checkKaessSitePermissions = useComponentLevelAccessCheck('add-site-button');
	const checkKaeTabSitePermissions = useComponentLevelAccessCheck('site-specialist-tab');
	const checkRbdmAddSitePermissions = useComponentLevelAccessCheck('add-new-site-btn-rbdm');
	const { isFetching: queryFetching, error } = useSiteSummaryListing();
	const { search, sortValue } = useFilterStore();
	const { allSiteSummaryFilter } = useSiteSummaryListingStore();
	const { isSiteStatusModalOpen, setSiteStatusModal } = useContractor360Store();
	const { clearSiteDetails } = useSiteAllDetailsStore();
	const { setToaster } = useTosterStore();
	const [isExporting, setIsExporting] = useState(false);
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const {
		result: data,
		isFetching,
		fetchMore,
		totalCount,
	} = useSiteSummaryRxDb({
		name: search,
		sortBy: sortValue,
		current_stage_of_site_c: allSiteSummaryFilter.current_stage_of_site_c,
		competition_site: allSiteSummaryFilter.competition_site,
		adopted_site: allSiteSummaryFilter.adopted_site,
		ssm_site: allSiteSummaryFilter.ssm_site,
		lastactivitydate: allSiteSummaryFilter.lastactivitydate,
		mdicode_c: allSiteSummaryFilter.mdicode,
	});
	const formik = useFormik({
		initialValues: {
			siteStatus: '',
		},
		onSubmit: () => {},
	});

	const { setCurrentStep } = useStepperStore();
	const { setSiteSfid, setSiteLeadId, setKeyBaseValue } = useSiteLeadStore();

	useInfiniteScroll(() => {
		fetchMore();
	});

	const handleRouteClick = (): void => {
		clearSiteDetails();
		setCurrentStep(0);
		setSiteSfid('');
		setSiteLeadId(null);
		setKeyBaseValue('addNewSiteLead', true);
		setKeyBaseValue('siteDetails', null);
	};
	let selectedTabList;

	if (!checkKaessSitePermissions) {
		selectedTabList = SiteSummaryTabKAESSUser;
	} else if (!checkKaeTabSitePermissions) {
		selectedTabList = SiteSummaryTabKAEUser;
	} else if (!checkRbdmAddSitePermissions) {
		selectedTabList = RBDMSiteSummaryTab;
	} else {
		selectedTabList = SiteSummaryTab;
	}

	const handleExportRBDMSites = async (): Promise<void> => {
		try {
			setIsExporting(true);
			const response = await APIService.getData<ISiteSummaryList[]>(APICONFIG.ALL_SITE_SUMMARY_LIST, {
				limit: '0',
			});

			if (response && data.length > 0) {
				const excelData: DataRow[] = response?.map((site) => ({
					ID: site.id ?? '',
					SFID: site.sfid ?? '',
					'Site Name': site.name ?? '',
					'Site Number': site.sitenumber_c ?? '',
					'Contractor Name': site.contract_name ?? '',
					'Contractor Phone': site.contract_phone ?? '',
					'Membership No': site.membership_no ?? '',
					'Current Stage': site.current_stage_of_site_c ?? '',
					'Site Current Status': site.site_current_status_c ?? '',
					'Competition Site': site.competition_site ? 'Yes' : 'No',
					'Adopted Site': site.adopted_site ? 'Yes' : 'No',
					'Brand of Glue Used': site.brand_of_glue_used_at_site ?? '',
					'Competition Products at Site': site.competition_products_at_site_c ?? '',
					'ROFF Substrate': site.roff_substrate_c ?? '',
					'Epoxy Adhesive Used': site.epoxy_adhesive_used_c ?? '',
					'Glue Potential': site.glue_potential ?? '',
					'FV Glue Used So Far': site.fv_glue_used_so_far ?? '',
					'Site Area': site.site_area ?? '',
					'Site Location': site.site_location_c ?? '',
					Latitude: site.latitude ?? '',
					Longitude: site.longitude ?? '',
					'Supervisor Name': site.supervisor_name ?? '',
					'Supervisor Phone': site.supervisor_phone_number ?? '',
					'Assigned CMDI': site.assigned_cmdi_c ?? '',
					'Primary CMDI': site.primary_cmdi ?? '',
					'CMDI Code': site.cmdi_code_c ?? '',
					'Created Date': formatDateToDDMMYYYY(site.createddate) ?? '',
					'Last Activity Date': formatDateToDDMMYYYY(site.lastactivitydate) ?? '',
				}));

				ExportXlsxFile(excelData, 'RBDM_SiteLeads');
				setToaster(true, ToastType.success, 'RBDM site leads exported successfully!');
			} else {
				setToaster(true, ToastType.error, 'No RBDM site leads data available for export');
			}
		} catch (error) {
			setToaster(true, ToastType.error, 'Failed to export RBDM site leads data');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			<section className="tab-search-section" id="site-summary">
				<div className="container">
					<NavigatorTab tabList={selectedTabList} selectedFilterChild={<SiteSummaryFilterTag count={totalCount} />}>
						<SiteSummarySearch
							setSortCallback={() => setOpenSort(true)}
							setFilterCallback={() => overlayAddFadeAction(() => setOpenFilter(true))}
						/>
					</NavigatorTab>
					<>
						{!checkRbdmAddSitePermissions && (
							<div className="export-btn-wrapper">
								<button onClick={handleExportRBDMSites} disabled={isExporting} className="btn btn-primary">
									{isExporting ? 'Exporting...' : 'Export RBDM Sites'}
								</button>
							</div>
						)}

						<Wrapper
							isError={error && new Error(`${error}`)}
							isLoaderVisible={queryFetching || (isFetching && data.length === 0)}
						>
							<div className="site-summary-card-wrapper">
								<ul className="row">
									{data && data.length !== 0 ? (
										data.map((site) => <SiteSummaryCard key={site.id} site={site} />)
									) : (
										<NoDataFound />
									)}
								</ul>
							</div>
						</Wrapper>
					</>
				</div>
			</section>
			{openSort && <Sort setSortOpen={() => overlayRemoveFadeAction(() => setOpenSort(false))} />}
			{openFilter && (
				<SiteSummaryFilters
					openFilters={openFilter}
					closeFilters={() => overlayRemoveFadeAction(() => setOpenFilter(false))}
				/>
			)}
			<Modal
				modalId="siteStatus"
				className="dialog dialog-sm site-summary-status-popup close-site-popup pt-4"
				modalOpen={isSiteStatusModalOpen}
				onModalClose={() => setSiteStatusModal(false)}
			>
				<SiteStatusModal formik={formik} />
			</Modal>
			{checkRbdmAddSitePermissions && !isImpersonationLoginUser && (
				<AddNewDataButton
					redirectUrl={checkKaessSitePermissions ? '/add-new-site' : '/add-new-site-ss'}
					labelName={'add new site lead'}
					handleClick={handleRouteClick}
				/>
			)}
		</>
	);
};
